import { useCallback, useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { StampKind } from '@wf/shared'
import { useAuth } from '../app/auth'
import { useAttendanceMe, useStamp } from '../attendance/useAttendance'
import { clearTimer, elapsedSeconds, getRunningTimer, startTimer, type RunningTimer } from './timerStore'
import { submitTime, type SubmitResult } from './submitTime'

export interface WorkTimer {
  /** Läuft der Timer für DIESEN Auftrag? */
  running: boolean
  /** Hält ein ANDERER Auftrag den (globalen) Lauf-Slot? → dessen orderId, sonst null. */
  busyOtherOrderId: number | null
  /** Verstrichene Sekunden, live (nur sinnvoll während `running`). */
  elapsed: number
  start: () => void
  stop: () => void
  /** Wird gerade gebucht/zugestellt? */
  submitting: boolean
  /** Ergebnis der letzten Buchung: 'sent' (gebucht) oder 'queued' (offline gesichert). */
  lastResult: SubmitResult | null
  /** Echter Ablehnungsfehler der Buchung (4xx) — Netz-/5xx-Fälle erscheinen NICHT hier, die landen in der Outbox. */
  error: unknown
  /** A3 Start-Gate: 'aus'/'pause' wenn der Start erst stempeln muss; null = frei (oder Stand unbekannt → nicht blocken). */
  attBlocked: 'aus' | 'pause' | null
  /** Ein-Tipp-Ausweg: stempelt (kommen bzw. pause_ende) und startet danach den Timer. */
  startWithStamp: () => void
  /** Läuft gerade der Stempel-Teil von startWithStamp? */
  stamping: boolean
  /** Fehler des Stempel-Teils (z. B. 409) — sichtbar machen, ein gescheiterter Ausweg darf kein stiller No-op sein. */
  stampError: unknown
}

/**
 * Persistenter Arbeits-Timer für einen Auftrag (Audit B-1/B-2/B-3). Lauf-State lebt in localStorage
 * (timerStore) → überlebt Reload/Standby. Stop bucht über die Offline-Outbox → kein Zeitverlust ohne Netz.
 * Die Buchung läuft über useMutation, damit ein 401 weiterhin zentral (MutationCache) behandelt wird (#1).
 */
export function useWorkTimer(orderId: number): WorkTimer {
  const qc = useQueryClient()
  const [timer, setTimer] = useState<RunningTimer | null>(() => getRunningTimer())
  const [now, setNow] = useState(() => Date.now())
  const [lastResult, setLastResult] = useState<SubmitResult | null>(null)

  const running = timer?.orderId === orderId
  const busyOtherOrderId = timer && timer.orderId !== orderId ? timer.orderId : null

  // Sekunden-Tick nur, solange DIESER Auftrag läuft.
  useEffect(() => {
    if (!running) return
    const handle = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(handle)
  }, [running])

  // Defensive Re-Synchronisation des In-Memory-States mit dem persistierten Stand: deckt den Fall ab,
  // dass ein anderer Tab den Slot belegt/freigegeben hat — oder die Stempel-Kopplung (A3, 'wf-timer-changed')
  // den Timer beim gehen/Pause-Stempel automatisch gestoppt hat.
  useEffect(() => {
    const sync = (): void => setTimer(getRunningTimer())
    window.addEventListener('pagehide', sync)
    window.addEventListener('wf-timer-changed', sync)
    document.addEventListener('visibilitychange', sync)
    return () => {
      window.removeEventListener('pagehide', sync)
      window.removeEventListener('wf-timer-changed', sync)
      document.removeEventListener('visibilitychange', sync)
    }
  }, [])

  const submit = useMutation({
    mutationFn: (vars: { seconds: number; startedAt?: number }) => submitTime(orderId, vars.seconds, vars.startedAt),
    onSuccess: (result) => {
      setLastResult(result)
      // Nur bei echter Zustellung den Server-Stand nachladen; 'queued' ändert serverseitig (noch) nichts.
      if (result === 'sent') void qc.invalidateQueries({ queryKey: ['orderTime', orderId] })
    },
  })

  const start = useCallback(() => {
    const startedAt = Date.now()
    if (startTimer(orderId, startedAt)) {
      setTimer({ orderId, startedAt })
      setNow(startedAt)
      setLastResult(null)
    } else {
      setTimer(getRunningTimer()) // Slot besetzt → aktuellen Stand übernehmen (zeigt den Hinweis an)
    }
  }, [orderId])

  const stop = useCallback(() => {
    const current = getRunningTimer()
    if (!current || current.orderId !== orderId) {
      setTimer(current)
      return
    }
    const seconds = elapsedSeconds(current, Date.now())
    clearTimer()
    setTimer(null)
    if (seconds < 1) return // unter einer Sekunde → nichts buchen
    submit.mutate({ seconds, startedAt: current.startedAt })
  }, [orderId, submit])

  // A3 Start-Gate: Auftragszeit zählt nur während gestempelter Arbeit. Bei 'aus'/'pause' wird der Start zum
  // Ein-Tipp-Ausweg (erst stempeln, dann starten) statt zur Sackgasse. Unbekannter Stand (Erst-Boot offline)
  // blockt NICHT — das Gate ist UX, die autoritative Prüfung läuft serverseitig bei der Buchung.
  // Superadmin/Support-Modus hat keine Stempeluhr (wie AttendanceBar): Gate aus, sonst liefe jeder Tipp in
  // den 403 „Im Support-Modus kann nicht gestempelt werden" (stiller No-op + Dead-Letter-Müll).
  const { user } = useAuth()
  const att = useAttendanceMe()
  const stamp = useStamp()
  const attBlocked = user && !user.isSuperadmin && att.data && att.data.status !== 'an' ? att.data.status : null

  const startWithStamp = useCallback(() => {
    const st = att.data
    if (!st || st.status === 'an') {
      start()
      return
    }
    const kind: StampKind = st.status === 'pause' ? 'pause_ende' : 'kommen'
    stamp.mutate({ kind, version: st.version }, { onSuccess: () => start() })
  }, [att.data, stamp, start])

  const elapsed = running && timer ? elapsedSeconds(timer, now) : 0

  return {
    running,
    busyOtherOrderId,
    elapsed,
    start,
    stop,
    submitting: submit.isPending,
    lastResult,
    error: submit.error,
    attBlocked,
    startWithStamp,
    stamping: stamp.isPending,
    stampError: stamp.error,
  }
}
